import React, { useRef, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import { FileDrop } from '../../../FileDrop/FileDrop'
import { API_S3_UPLOAD } from '../../../../consts'
import { ArrayFieldLabelStyle } from '../../styles'
import { RevertToDefaultButton } from '../../../Localizations/RevertToDefaultButton'
import { compact, isEqual } from 'lodash'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { uploadFileToS3 } from '../../../../utils/uploadFileToS3'
import { DropNotification } from './DropNotification'
import { SortablePhoto } from './SortablePhoto'
import { UploadingNotification } from './UploadingNotification'
import { ImageErrors } from './ImageErrors'
import { ImageWarnings } from './ImageWarnings'

const validFileType = (file: File, types: string[]) => {
  return types.includes(file.type)
}

const validFileSize = (file: File, maxSize: number) => {
  return file.size <= maxSize
}
const validDimensions = (image: HTMLImageElement) => {
  return image.naturalWidth === image.naturalHeight
}

const validImageSize = (image: HTMLImageElement, minSize: number) => {
  return image.naturalWidth >= minSize || image.naturalHeight >= minSize
}

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']
const MAX_FILE_SIZE = 4 * (1024 * 1024) // 4MB
const MIN_SIZE = 1000

/**
 * Component to display and modify product and localization images.
 * @param onChange - The onChange function for the FinalForm input
 * (input.onChange)
 * @param {string[]} [image_urls] This is the image_urls state from the form
 * @param {string[]} [productImageUrls] - (Optional) When used for localizations
 * provide the component with the product's image_urls so the component can know
 * if it's displaying the default values or not. If this parameter is omitted no
 * button will be displayed.
 */

export const Photos = ({
  onChange,
  image_urls,
  productImageUrls,
}: {
  onChange: (e: React.ChangeEventHandler<HTMLInputElement> | string[]) => void
  image_urls: string[]
  productImageUrls?: string[]
}) => {
  const filePicker = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState<Number>(0)
  const [imageErrors, setImageErrors] = useState<string[]>([])
  const [imageWarnings, setImageWarnings] = useState<string[]>([])

  const addFiles = async (files: FileList) => {
    setImageErrors([])
    setImageWarnings([])
    setUploading(files.length)

    const urls = await Promise.all(
      [...files].map(async (file) => {
        const FILE_TYPE_ERROR = `${file.name} failed to upload! Photos must be PNG or JPEG files.`
        const FILE_SIZE_ERROR = `${file.name} failed to upload! Photos must be less than 4MB.`
        const DIMENSIONS_WARNING = `${file.name} Photo does not have square dimensions.`
        const SQUARE_WARNING = `${file.name} Photos is less than ${MIN_SIZE}px by ${MIN_SIZE}px in size.`

        if (!validFileType(file, ALLOWED_FILE_TYPES)) {
          setImageErrors((messages) => [...messages, FILE_TYPE_ERROR])
          return
        }

        if (!validFileSize(file, MAX_FILE_SIZE)) {
          setImageErrors((messages) => [...messages, FILE_SIZE_ERROR])
          return
        }

        const onFileLoad = async (event: ProgressEvent<FileReader>) => {
          const onImageLoad = async (event: Event) => {
            const image = event.target as HTMLImageElement

            if (!validDimensions(image)) {
              setImageWarnings((messages) => [...messages, DIMENSIONS_WARNING])
              return
            }

            if (!validImageSize(image, MIN_SIZE)) {
              setImageWarnings((messages) => [...messages, SQUARE_WARNING])
              return
            }
          }

          const image = new Image()
          image.addEventListener('load', onImageLoad)
          image.src = event.target?.result as string
        }

        const reader = new FileReader()
        reader.addEventListener('loadend', onFileLoad)
        reader.readAsDataURL(file)

        const url = await uploadFileToS3(API_S3_UPLOAD, file, {
          credentials: 'include',
        })

        return url
      })
    )

    const cleaned = compact(urls)

    onChange([...image_urls, ...cleaned])
    setUploading(0)
  }

  const addImageClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    filePicker.current?.click()
  }

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  )

  const handleDragEnd = (event: any) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = image_urls.indexOf(active.id)
      const newIndex = image_urls.indexOf(over.id)
      const updated = arrayMove(image_urls, oldIndex, newIndex)

      onChange(updated)
    }
  }

  const removeImage = (url: string) => {
    const newImages = [...image_urls].filter((el) => el !== url)

    onChange(newImages)
  }

  return (
    <FileDrop onFilesAdded={addFiles}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <ArrayFieldLabelStyle>Photos</ArrayFieldLabelStyle>
        <input
          name="photos"
          type="file"
          value={''}
          multiple
          style={{ display: 'none' }}
          onChange={onFilesPicked}
          ref={filePicker}
        />
        <button
          type="button"
          style={{ marginLeft: 10, display: 'inline-block' }}
          onClick={addImageClick}
        >
          <Icon name="add" size="small" />
          Add
        </button>

        {productImageUrls && (
          <RevertToDefaultButton
            style={{ marginLeft: 10 }}
            isDefault={isEqual(image_urls, productImageUrls)}
            handleClick={() => {
              onChange(productImageUrls)
            }}
          />
        )}

        <ImageErrors errors={imageErrors} />
        <ImageWarnings warnings={imageWarnings} />
        <UploadingNotification uploading={uploading} />
        <DropNotification />

        <SortableContext items={image_urls} strategy={rectSortingStrategy}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              maxWidth: 600,
              minHeight: 180,
              maxHeight: 500,
              overflow: 'auto',
              outline: 'lightgray auto thin',
              padding: '10px',
            }}
          >
            {image_urls?.map((url) => (
              <SortablePhoto key={url} url={url} removeImage={removeImage} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </FileDrop>
  )
}
